/**
 * @description Channel Partner Settings — Branding | Domains (LYN-309 / LYN-340)
 *
 * @dependencies BrandingEditor, primary + sending domain panels, channelPartnerApi
 * @notes Branding is Identity / Colors / Assets / Meta / Preview. Domains holds
 *   Primary (340) and Sending (308). Same writers as Super Admin. Not My Company.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BrandingEditor from '../../components/channel-partner/BrandingEditor';
import ChannelPartnerPrimaryDomainPanel from '../../components/channel-partner/ChannelPartnerPrimaryDomainPanel';
import ChannelPartnerEmailSendingDomainPanel from '../../components/channel-partner/ChannelPartnerEmailSendingDomainPanel';
import {
  deleteChannelPartnerFavicon,
  deleteChannelPartnerLogo,
  deleteChannelPartnerLogoMark,
  getChannelPartnerMe,
  updateChannelPartnerBranding,
  uploadChannelPartnerFavicon,
  uploadChannelPartnerLogo,
  uploadChannelPartnerLogoMark,
} from '../../services/api/channelPartnerApi';

const SETTINGS_TABS = [
  { key: 'branding', label: 'Branding' },
  { key: 'domains', label: 'Domains' },
];

export default function ChannelPartnerSettings() {
  const location = useLocation();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [error, setError] = useState(null);

  const currentTab = useMemo(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (SETTINGS_TABS.some((item) => item.key === tab)) {
      return tab;
    }
    return 'branding';
  }, [location.search]);

  const reload = async () => {
    const res = await getChannelPartnerMe();
    setPartner(res.data);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getChannelPartnerMe();
        if (!cancelled) setPartner(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error?.message || 'Failed to load settings');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleTabClick = (tabKey) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', tabKey);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  if (error) {
    return <div className="p-6 text-sm text-red-600">{error}</div>;
  }
  if (!partner) {
    return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Settings</h1>
      <div className="border-b mb-6">
        <nav className="flex space-x-8">
          {SETTINGS_TABS.map((tab) => {
            const selected = currentTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabClick(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  selected
                    ? 'border-brand text-brand'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {currentTab === 'branding' && (
        <BrandingEditor
          branding={partner.branding || {}}
          onSave={async (payload) => {
            await updateChannelPartnerBranding(payload);
            await reload();
          }}
          onUpload={async (kind, file) => {
            if (kind === 'logo') await uploadChannelPartnerLogo(file);
            else if (kind === 'logoMark') await uploadChannelPartnerLogoMark(file);
            else await uploadChannelPartnerFavicon(file);
            await reload();
          }}
          onDelete={async (kind) => {
            if (kind === 'logo') await deleteChannelPartnerLogo();
            else if (kind === 'logoMark') await deleteChannelPartnerLogoMark();
            else await deleteChannelPartnerFavicon();
            await reload();
          }}
        />
      )}

      {currentTab === 'domains' && (
        <div className="space-y-6">
          <ChannelPartnerPrimaryDomainPanel />
          <ChannelPartnerEmailSendingDomainPanel />
        </div>
      )}
    </div>
  );
}
